import { createContext, useContext, useEffect, useState, useMemo } from "react";
import api from "@/lib/api";

/**
 * TenantContext
 * -------------
 * Phase B (current): tenant is derived from the URL path — routes prefixed with
 *   `/t/:slug/*` render as a tenant-branded shell. `/t/empresa1` fetches the
 *   `empresa1` org branding (logo + primary color) and injects it site-wide.
 * Phase A (subdomain, when *.sprintroll.app wildcard DNS is live):
 *   swap the `detectTenantSlug` function to read `window.location.hostname`.
 *   Everything else stays the same.
 */
const TenantContext = createContext({
  tenant: null,
  isTenant: false,
  loading: false,
  error: null,
});

// Root hosts that DO NOT resolve a subdomain tenant.
const ROOT_HOSTS = new Set([
  "sprintroll.app", "www.sprintroll.app",
  "localhost", "127.0.0.1",
]);

function detectTenantFromHost() {
  const host = (typeof window !== "undefined" ? window.location.hostname : "") || "";
  if (!host || ROOT_HOSTS.has(host)) return null;
  // Root-hosted single-tenant preview URLs → ignore.
  if (host.endsWith(".preview.emergentagent.com")) return null;
  // Any *.sprintroll.app that isn't in ROOT_HOSTS is a tenant subdomain.
  if (host.endsWith(".sprintroll.app")) {
    const sub = host.split(".")[0];
    if (sub && sub !== "www") return sub;
  }
  return null;
}

function detectTenantFromPath() {
  const path = (typeof window !== "undefined" ? window.location.pathname : "") || "";
  const m = path.match(/^\/t\/([a-z0-9][a-z0-9-]{1,38}[a-z0-9])(?:\/|$)/);
  return m ? m[1] : null;
}

export function TenantProvider({ children }) {
  const [tenant, setTenant] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    const slug = detectTenantFromHost() || detectTenantFromPath();
    if (!slug) { setLoading(false); return; }
    let mounted = true;
    (async () => {
      try {
        const { data } = await api.get(`/tenant/by-slug/${slug}`);
        if (mounted) setTenant(data);
      } catch (e) {
        if (mounted) setError(e?.response?.status === 404 ? "not_found" : "load_failed");
      } finally {
        if (mounted) setLoading(false);
      }
    })();
    return () => { mounted = false; };
  }, []);

  // Inject CSS variable + favicon whenever the tenant changes.
  useEffect(() => {
    const root = document.documentElement;
    const color = tenant?.branding?.primary_color;
    if (color) {
      const hsl = hexToHsl(color);
      root.style.setProperty("--brand-primary", color);
      root.style.setProperty("--brand-primary-rgb", hexToRgb(color));
      if (hsl) {
        root.style.setProperty("--primary", hsl);
        root.style.setProperty("--ring", hsl);
      }
    } else {
      root.style.removeProperty("--brand-primary");
      root.style.removeProperty("--brand-primary-rgb");
      root.style.removeProperty("--primary");
      root.style.removeProperty("--ring");
    }
    // Update page title with tenant name for a nicer UX
    if (tenant?.name) {
      document.title = `${tenant.name} · Sprintroll`;
    }
  }, [tenant]);

  const value = useMemo(() => ({
    tenant,
    isTenant: !!tenant,
    loading,
    error,
  }), [tenant, loading, error]);

  return <TenantContext.Provider value={value}>{children}</TenantContext.Provider>;
}

export function useTenant() {
  return useContext(TenantContext);
}

function hexToRgb(hex) {
  const m = hex.match(/^#([0-9a-f]{6})$/i);
  if (!m) return "0,0,0";
  const n = parseInt(m[1], 16);
  return `${(n >> 16) & 255},${(n >> 8) & 255},${n & 255}`;
}

/** Convert #RRGGBB to shadcn "H S% L%" (no hsl() wrapper). */
function hexToHsl(hex) {
  const m = hex.match(/^#([0-9a-f]{6})$/i);
  if (!m) return null;
  const n = parseInt(m[1], 16);
  const r = ((n >> 16) & 255) / 255;
  const g = ((n >> 8) & 255) / 255;
  const b = (n & 255) / 255;
  const max = Math.max(r, g, b), min = Math.min(r, g, b);
  let h = 0, s = 0;
  const l = (max + min) / 2;
  if (max !== min) {
    const d = max - min;
    s = l > 0.5 ? d / (2 - max - min) : d / (max + min);
    switch (max) {
      case r: h = (g - b) / d + (g < b ? 6 : 0); break;
      case g: h = (b - r) / d + 2; break;
      case b: h = (r - g) / d + 4; break;
      default: break;
    }
    h /= 6;
  }
  return `${Math.round(h * 360)} ${Math.round(s * 100)}% ${Math.round(l * 100)}%`;
}
